// @flow
import React, { Component } from 'react';
import styled from 'react-emotion';
import { VelocityComponent } from 'velocity-react';
import consts, { colors, fonts } from '../assets/scripts/constants';
import {
  buildDummyChangeEvent,
  smoothScrollIntoView,
} from '../assets/scripts/helpers';
import iconDash from '../assets/images/icon_dash.svg';
import Button from './Button';

/**
 * Styles
 */

const duration = 0.3; // Animation duration in seconds

const Wrapper = styled('div')`
  display: ${({ isHidden }) => isHidden && 'none'};
  margin-bottom: ${consts.cellEm};

  > button {
    position: relative;
    width: 100%;
    padding: 0;
    box-sizing: content-box;
    font-family: ${fonts.primary};
    font-size: 1.4em;
    font-weight: 600;
    line-height: 1.75;
    text-transform: uppercase;
    border: none;
    background: ${({ isOpen, isSelected }) =>
      isOpen || isSelected ? colors.orange : colors.gray};
    transition: background-color ${duration}s, box-shadow ${duration}s;
    color: ${colors.white};
    cursor: pointer;

    &:focus {
      box-shadow: inset 0px 4px 14px 0px rgba(0, 0, 0 , 0.2);
      outline: 0;
    }

    &[disabled] {
      cursor: auto;
    }

    &::before,
    &::after {
      content: url('${iconDash}');
      position: absolute;
      top: -0.18em;
      right: 0.5em;
      display: inline-block;
      width: 12px;
      height: 24px;
      transition: transform ${duration}s, opacity ${duration}s;
      pointer-events: none;
    }

    &::before {
      right: 0.48em;
      transform: ${({ isOpen }) =>
        isOpen ? '' : 'rotate(90deg) translateY(-0.25em) translateX(0.2em)'};
    }

    &::after {
      opacity: ${({ isOpen }) => (isOpen ? '0' : '1')};
    }
  }

  > div {
    padding: ${consts.cellEm} ${0.5 * consts.cell}em 0;
  }
`;

/**
 * Component: Slider
 *
 * Configurations a displayed inside this as dropdown sliders.
 */
class Slider extends Component<{
  children: Object,
  disabled: boolean,
  handleConfigChange: Function,
  handleClick: Function,
  isHidden: boolean,
  isOpen: boolean,
  // prettier-ignore
  isPreselected: boolean | {
    description: string,
    label: string,
    price: string,
    slug: string,
    time: string,
    title: string,
    value: string,
  },
  isSelected: mixed,
  parent: Object,
  sliderId: number,
  title: string,
}> {
  componentDidMount() {
    this.checkIfPreselected();
  }

  componentDidUpdate() {
    this.checkIfPreselected();
  }

  /**
   * If we are opening a `Select`-including slider for the first time, check if
   * there is a preselected slot we want to add to current config selections.
   */
  checkIfPreselected() {
    const {
      handleClick,
      handleConfigChange,
      isHidden,
      isOpen,
      isPreselected,
      isSelected,
      sliderId,
    } = this.props;
    // Handling preselection by a dummy event and node.
    if (isOpen && typeof isPreselected === 'object' && !isSelected) {
      const {
        description,
        id,
        label,
        price,
        slug,
        time,
        title,
        value,
      } = isPreselected;
      handleConfigChange(
        buildDummyChangeEvent({
          description,
          id,
          label,
          price,
          slug,
          time,
          title,
          value,
        })
      );
    }
    // Skip to next slider if hidden.
    if (isOpen && isHidden) {
      handleClick(sliderId + 1);
    }
  }

  render() {
    const {
      children,
      disabled,
      handleClick,
      isHidden,
      isOpen,
      isSelected,
      sliderId,
      parent,
      title,
    } = this.props;
    const animation = `slide${isOpen ? 'Down' : 'Up'}`;
    return (
      <Wrapper isHidden={isHidden} isOpen={isOpen} isSelected={isSelected}>
        <button onClick={() => handleClick(sliderId)} disabled={disabled}>
          {title}
        </button>
        <VelocityComponent
          key={sliderId}
          animation={animation}
          duration={duration * 1000}
          interruptBehavior="queue"
          complete={el => {
            // Smoothly scroll OK button into view.
            if (parent && parent.current && el && el[0] && el[0].scrollHeight) {
              const buttons = el[0].querySelectorAll('button');
              if (buttons.length) {
                smoothScrollIntoView(
                  buttons[buttons.length - 1],
                  parent.current
                );
              }
            }
          }}
        >
          <div>
            {children}
            <Button
              onClick={() => handleClick(sliderId + 1)}
              disabled={!isSelected}
            >
              OK
            </Button>
          </div>
        </VelocityComponent>
      </Wrapper>
    );
  }
}

export default Slider;



// WEBPACK FOOTER //
// ./src/components/Slider.js